import { countries, CountryCode } from "@redotech/locale/countries";
import { Currency } from "@redotech/money/currencies";
import { moneyFormat } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { getShopCurrencyWithDefault } from "@redotech/redo-billing-common/team-currency";
import { uncoveredSalesChannelWarningSchema as adminUncoveredSalesChannelWarningSchema } from "@redotech/redo-order-common/sales-channels-schema";

import { Onboarding } from "@redotech/account-common/onboarding";
import {
  Settings as AuditableSettings,
  getChanges,
} from "@redotech/account-common/team-settings-audit";
import { CustomerPortalVersion } from "@redotech/congo-enums/customer-portal-version";
import { NonZeroValueExchangeStrategy } from "@redotech/redo-common-model/exchange";
import { isCustomRateTable } from "@redotech/redo-conversion-core-common/redo-in-shipping/shipping-rate-table";
import { RedoItemFulfillment } from "@redotech/redo-fulfillment-common/redo-item-fulfillment";
import { MAX_MERCHANT_FEATURE_PAUSE_DAYS } from "@redotech/redo-merchant-pause-common/paused-feature";
import { ShippingInsurance } from "@redotech/redo-package-protection-common/shipping-insurance";
import { ShopSiteNavigationMode } from "@redotech/redo-return-core-common/exchange-settings";
import { SendcloudReturnDocumentType } from "@redotech/redo-return-core-common/shipping/sendcloud-return-document-type";
import { CoverageBillingTrigger } from "@redotech/redo-return-coverage-common/coverage-billing-trigger";
import { returnsCoverageIsEnabled } from "@redotech/redo-return-coverage-common/coverage-enabled";
import { CoverageMonitor as CoverageMonitorType } from "@redotech/redo-return-coverage-common/coverage-monitor";
import { PackageProtectionPricingStrategy } from "@redotech/redo-return-coverage-common/coverage-types";
import { MerchantCarrierReturnLabelMode } from "@redotech/redo-return-settings-primitives-common/merchant-carrier-return-label-mode";
import { CoverageProductEnum } from "@redotech/redo-ris-enums-common/ris-enums";
import {
  getCoverageVariantNaming,
  getCoverageVariantNamingToSave,
} from "@redotech/redo-shopify-settings-common/shopify-extension-settings";
import {
  AttachmentStrategy,
  doesAttachmentStrategyRenderInCart,
  isAttachmentStrategyCheckoutExtensionEnabled,
} from "@redotech/redo-storefront-common/attachment-strategy";
import { InfoModalVersion } from "@redotech/redo-storefront-common/info-modal-version";
import { InsertionPosition } from "@redotech/redo-storefront-common/insertion-position";
import type { StorefrontPlatform } from "@redotech/redo-storefront-common/storefront-config";
import { Team } from "@redotech/redo-team-common/team";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { PageForm, PageFormSave } from "@redotech/redo-web/page-form";
import { SettingSection } from "@redotech/redo-web/setting-section/setting-section";
import { SettingSectionTabNav } from "@redotech/redo-web/setting-section/setting-section-tab-nav";
import { TabNavigationProvider } from "@redotech/redo-web/setting-section/tab-navigation-provider";
import {
  finalSaleReturnsDefault,
  finalSaleReturnsForm,
  finalSaleReturnsFormValueToTeam,
  finalSaleReturnsTeamToFormValue,
  FinalSaleReturnsValue,
} from "@redotech/redo-web/settings-elements/final-sale";
import {
  packageProtectionDefault,
  packageProtectionFormValueToTeam,
  packageProtectionFormWithoutPpPlusPriceFloor,
  packageProtectionTeamToFormValue,
  PackageProtectionValue,
} from "@redotech/redo-web/settings-elements/package-protection-elements";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { assertNever } from "@redotech/util/type";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import {
  memo,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import type * as z from "zod";
import { RedoAdminRpcClientContext } from "../../../../app/redo-admin-rpc-client-provider";
import { RedoAdminClientContext } from "../../../../client/context";
import {
  getCoverageMonitoring,
  resetCoverageMonitoring,
} from "../../../../client/coverage-monitoring";
import { EcommerceProviderIntegrationContext } from "../../../../client/ecommerce-provider-integration-context";
import { getOnboarding } from "../../../../client/onboarding";
import { notifySlack } from "../../../../client/orders";
import { getTeam, updateTeam } from "../../../../client/team";
import { useMerchantAppLogin } from "../../../merchant-app";
import { PreviewButton } from "../../../split/preview-button";
import { LoadTeamContext, TeamContext } from "../../../team";
import { CopyLatestFlowSettings } from "../../shared/copy-latest-flow";
import { CopyPortalCustomizationSettings } from "../../shared/copy-portal-customization";
import { StorefrontPublishConfigurator } from "../../storefront-publishing/storefront-publish-configurator";
import { TeamChangesTooltip } from "../component/team-changes-tooltip";
import { risDefault, risForm, RisFormValue } from "../coverage-product";
import {
  createExchangeAgentBillingSettingsFormValue,
  exchangeAgentBillingConfigSettingsDefault,
  ExchangeAgentBillingSettingsCard,
  exchangeAgentBillingSettingsForm,
  getUpdatedExchangeAgentBillingSettings,
} from "../exchange-agent-billing-settings";
import {
  LabelRatesCard,
  returnLabelsDefault,
  returnLabelsForm,
} from "../label-rate/label-rates-card";
import { ShippingZones } from "../redo-in-shipping/shipping-zones";
import {
  returnsCoverageDefault,
  returnsCoverageForm,
  returnsCoverageTeamToFormValue,
  ReturnsCoverageValue,
  ReturnsPricingEnum,
} from "../returns/coverage";
import { storefrontDefault, storefrontForm } from "../storefront";
import {
  yofiDefault,
  yofiForm,
  yofiFormValueToTeam,
  YofiIntegrationCard,
  yofiTeamToFormValue,
} from "../yofi-integration";
import { AttachmentStrategyPicker } from "./component/attachment-strategy-picker";
import { LinkCard } from "./component/link-card";
import { ProtectionProductSummary } from "./component/protection-product-summary";
import { SettingCard } from "./component/setting-card";
import {
  AddWidgetButton,
  PDPWidgetItem,
  ToggleWidgetItem,
  WidgetSelect,
} from "./component/widget-select";
import { CoveragePauseCard } from "./coverage-pause-card";
import { CoverageQaCard } from "./coverage-qa-card";
import * as styles from "./general.module.css";
import { OnboardingOrderCountCard } from "./onboarding-order-count-card";
import { OnboardingStats } from "./onboarding-stats";
import {
  additionalCoverageWebSourcesDefault,
  additionalCoverageWebSourcesForm,
  AdditionalCoverageWebSourcesSettings,
} from "./section/additional-coverage-web-sources";
import {
  billingTypeDefault,
  billingTypeForm,
  BillingTypeSettings,
  CoverageBillingSettings,
} from "./section/billing-type-settings";
import { CoverageMonitorSettings } from "./section/coverage-monitor-settings";
import {
  ExchangeOutboundLabelCreditSettingsCard,
  exchangeOutboundLabelCreditSettingsForm,
} from "./section/exchange-outbound-label-credit-settings";
import {
  bundleRulesToJson,
  parseBundleRulesJson,
  ReturnsGeneralCard,
  returnsGeneralDefault,
  returnsGeneralForm,
} from "./section/general";
import {
  PerReturnBillingSettingsCardV2,
  perReturnBillingSettingsDefault,
  perReturnBillingSettingsForm,
  perReturnBillingSettingsFormTeamToValue,
  perReturnBillingSettingsFormValueToTeam,
} from "./section/per-return-billing-settings";
import {
  ReturnShippingSettings,
  shippingDefault,
  shippingForm,
} from "./section/shipping-settings";
import { SubscriptionBillingCardV2 } from "./section/subscription-billing";
import {
  subscriptionBillingDefault,
  subscriptionBillingForm,
  subscriptionBillingFormValueToTeam,
  subscriptionBillingSettingsToFormValue,
} from "./section/subscription-billing-form";
import { STOREFRONT_PLATFORM_BY_PROVIDER } from "./storefront-platform";

// type fixedOrPercentageType = "fixed" | "percentage";

const returnsPackageProtectionForm = groupInput({
  returnsGeneral: returnsGeneralForm,
  returnsCoverage: returnsCoverageForm,
  packageProtection: packageProtectionFormWithoutPpPlusPriceFloor,
  finalSaleReturns: finalSaleReturnsForm,
  yofi: yofiForm,
  shipping: shippingForm,
  billingType: billingTypeForm,
  storefront: storefrontForm,
  ris: risForm,
  returnLabels: returnLabelsForm,
  additionalCoverageWebSources: additionalCoverageWebSourcesForm,
  exchangeAgentBillingSettings: exchangeAgentBillingSettingsForm,
  subscriptionBilling: subscriptionBillingForm,
  perReturnBillingSettings: perReturnBillingSettingsForm,
  exchangeOutboundLabelCreditSettings: exchangeOutboundLabelCreditSettingsForm,
  redoMaxLabelPrice: input<string>(),
  multipleLabelsEnabled: input<boolean>(),
});

type ReturnsPackageProtectionValue = InputSchema.Value<
  typeof returnsPackageProtectionForm
>;

const returnsPackageProtectionDefault: ReturnsPackageProtectionValue = {
  returnsGeneral: returnsGeneralDefault,
  returnsCoverage: returnsCoverageDefault,
  packageProtection: packageProtectionDefault,
  finalSaleReturns: finalSaleReturnsDefault,
  yofi: yofiDefault,
  shipping: shippingDefault,
  billingType: billingTypeDefault,
  storefront: storefrontDefault,
  ris: risDefault,
  returnLabels: returnLabelsDefault,
  additionalCoverageWebSources: additionalCoverageWebSourcesDefault,
  exchangeAgentBillingSettings: exchangeAgentBillingConfigSettingsDefault,
  subscriptionBilling: subscriptionBillingDefault,
  perReturnBillingSettings: perReturnBillingSettingsDefault,
  exchangeOutboundLabelCreditSettings: { enabled: false, amount: "0" },
  redoMaxLabelPrice: "",
  multipleLabelsEnabled: false,
};

export const ReturnsGeneralPage = memo(function ReturnsGeneralPage() {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const client = useRequiredContext(RedoAdminClientContext);
  const params = useParams();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const teamId = params.teamId!;
  const team = useContext(TeamContext);
  const loadTeam = useRequiredContext(LoadTeamContext);

  const [onboardingLoad, loadOnboarding] = useTriggerLoad((signal) =>
    getOnboarding(client, { teamId, signal }),
  );

  const [risIntegrationLoad, loadRisIntegration] = useTriggerLoad((signal) =>
    rpcClient.getShopifyIntegrationSettings(
      { teamId, integrationType: "Shopify" },
      { signal },
    ),
  );

  const ecommerceProviderIntegration = useContext(
    EcommerceProviderIntegrationContext,
  );
  const storefrontPlatform = ecommerceProviderIntegration.primaryProvider
    ? STOREFRONT_PLATFORM_BY_PROVIDER[
        ecommerceProviderIntegration.primaryProvider
      ]
    : undefined;

  //Whether or not this merchant has access to carrier services
  const hasCarrierServices = useLoad(
    async (signal) => {
      if (risIntegrationLoad.value?.hasShopifyIntegration) {
        return (await rpcClient.hasCarrierServices({ teamId }, { signal }))
          .hasCarrierServices;
      }
      return false;
    },
    [rpcClient, risIntegrationLoad.value],
  );

  const [coverageMonitoringLoad, loadCoverageMonitoring] = useTriggerLoad(
    (signal) => getCoverageMonitoring(client, { teamId, signal }),
  );

  const uncoveredWarningsLoad = useLoad(
    (signal) =>
      rpcClient.getUncoveredSalesChannelWarnings({ teamId }, { signal }),
    [rpcClient, teamId],
  );

  const handleSave = useHandler(() => {
    loadTeam();
    loadOnboarding();
    loadCoverageMonitoring();
    loadRisIntegration();
  });

  const value = useMemo(() => {
    if (!team) {
      return undefined;
    }
    if (!risIntegrationLoad.value && !risIntegrationLoad.error) {
      return undefined;
    }
    const yofiFormValue = yofiTeamToFormValue(team);

    // Debug AI Exchange settings initialization
    const risForm: RisFormValue = risIntegrationLoad.value?.risIntegration
      ? {
          ...risIntegrationLoad?.value?.risIntegration,
          coverage: risIntegrationLoad.value?.risIntegration?.coverage
            ? risIntegrationLoad.value?.risIntegration?.coverage
            : CoverageProductEnum.SHIPPING,
          deliveryFilterType:
            risIntegrationLoad.value.risIntegration.deliveryFilterType,
          deliverySortType:
            risIntegrationLoad.value.risIntegration.deliverySortType,
        }
      : risIntegrationLoad.value?.hasShopifyIntegration
        ? risDefault
        : undefined;
    return {
      returnProcessing: {
        shopAgain: team.settings.shopAgainAction,
        riseTriggerUrl: team.settings.riseTrigger?.url || "",
        portalExcludedProductTags:
          team.settings.portalExcludedProductTags || [],
        portalExcludedCollections:
          team.settings.portalExcludedCollections || [],
        portalExcludedProductIds: team.settings.portalExcludedProductIds || [],
        allowReopen: team.settings.returns?.allowReopenAfterProcessing ?? false,
        hideGiftCardBranding: !!team.settings.returns?.hideGiftCardBranding,
        disableRiseCreditEmail: !!team.settings.returns?.disableRiseCreditEmail,
        allowRestockBeforeProcess:
          !!team.settings.inventory?.allowRestockBeforeProcess,
        checkForTryNowTags: !!team.settings.returns?.checkForTryNowTags,
      },
      returnsGeneral: {
        enabled: team.settings?.returns?.enabled ?? true,
        customerPortalVersion:
          team.settings?.customerPortalVersion || CustomerPortalVersion.V40,
        refundAllReturnedItems:
          team.settings?.returns?.refundAllReturnedItems ?? false,
        skipExchangeRefunds:
          team.settings?.returns?.skipExchangeRefunds ?? false,
        settlementEnabled: team.settings?.returns?.settlementEnabled ?? false,
        settlementRefund: (
          (team.settings?.returns?.settlementRefund || 0) * 100
        ).toString(),
        allowMultiOrderReturns:
          team.settings.returns?.allowMultiOrderReturns ?? false,
        multiOrderReturnLimit: team.settings.returns?.multiOrderReturnLimit
          ? String(team.settings.returns.multiOrderReturnLimit)
          : "0",
        chargeMerchantForDamagedOrWrongProductReturnLabels:
          !team.settings.returns
            ?.dontChargeMerchantForDamagedOrWrongProductReturnLabels,
        enableCustomerPortal:
          team.settings.returns?.enableCustomerPortal ?? true,
        enableShopAppReturns:
          team.settings.returns?.enableShopAppReturns ?? false,
        buyYourOwnLabel: team.settings.returns?.buyYourOwnLabel ?? false,
        nonZeroValueExchangeStrategy:
          team.settings.exchanges?.nonZeroValueExchangeStrategy ??
          NonZeroValueExchangeStrategy.NONE,
        agentReviewEnabled: team.settings?.returns?.agentReviewEnabled ?? false,
        agentChatEnabled: team.settings?.returns?.agentChatEnabled ?? false,
        sendStoreCreditCreatedForGiftCards:
          team.settings?.returns?.sendStoreCreditCreatedForGiftCards ?? false,
        exchangePaymentStrategy:
          risIntegrationLoad.value?.exchangePaymentStrategy ?? null,
        shopifyDeferReturnItemProcessing:
          team.settings.exchanges?.shopifyDeferReturnItemProcessing ?? false,
        orderSkuMetafieldKey:
          team.settings.returns?.orderSkuMetafieldKey ?? undefined,
        bundleRulesJson: bundleRulesToJson(team.settings.bundleRulesList),
        dutyDrawbackEnabled:
          team.settings.returns?.dutyDrawback?.enabled ?? false,
        dutyDrawbackEligibleCountries: (
          team.settings.returns?.dutyDrawback?.eligibleCountries ?? []
        ).flatMap((code) => {
          const country = countries.find((c) => c.code === code);
          return country ? [country] : [];
        }),
      },
      returnsCoverage: returnsCoverageTeamToFormValue(team),
      packageProtection: packageProtectionTeamToFormValue(team),
      finalSaleReturns: finalSaleReturnsTeamToFormValue(team),
      exchanges: {
        selection: team.settings.exchanges.selection,
        checkoutSelector: team.settings.checkoutSelector ?? "",
        exchangeBannerColor: team.settings.exchangeBannerColor || "",
        exchangeBannerPositionBottom:
          team.settings?.shopifyExtension?.exchangeBannerPositionBottom ||
          false,
        exchangeBannerInsertBeforeBody:
          team.settings?.shopifyExtension?.exchangeBannerInsertBeforeBody ||
          false,

        exchangeBannerText: team.settings.exchangeBannerText || "",
        exchangeBannerFont: team.settings.exchangeBannerFont || "",
        exchangeBannerFontColor: team.settings.exchangeBannerFontColor || "",
        exchangeBannerShowDoneButton:
          team.settings.exchangeBannerShowDoneButton ?? true,
        exchangeBannerCustomCss: team.settings.exchangeBannerCustomCss || "",
        exchangeOptionsPreviewShowOriginalPrice:
          team.settings.exchanges.exchangeOptionsPreviewShowOriginalPrice ??
          false,
        shopSiteNewTab:
          team.settings.exchanges.shopSite.navigationMode ===
          ShopSiteNavigationMode.NEW_TAB,
      },
      yofi: yofiFormValue,
      shipping: {
        carrierOverride: team.settings.shippingCarrierOverride,
        includeMultipleCarrierOptions:
          team.settings.includeMultipleCarrierOptions ?? false,
        passThroughReturnLabelAdjustments:
          team.settings.returns?.passThroughLabelAdjustments ?? false,
        pickupEnabled: team.settings.pickupEnabled,
        pickupEnabledForHeavyItems:
          team.settings.pickupEnabledForHeavyItems ?? false,
        pickupMaxLabelUpgradeRange:
          team.settings.pickupMaxLabelUpgradeRange?.toString() ?? "",
        enableOnHoldOrders: !!team.settings.exchanges.enableOnHoldOrders,
        onHoldOrdersOnUpsellOnly:
          !!team.settings.exchanges.onHoldOrdersOnUpsellOnly,
        insured: team.settings.shippingInsurance === ShippingInsurance.ENABLED,
        canEditReturnAddress: team.settings.canEditReturnAddress ?? false,
        generateQrCodes: team.settings.generateQrCodes,
        qrCodeOnlyShipping: team.settings.qrCodeOnlyShipping ?? false,
        sendcloudReturnDocumentType:
          team.settings.sendcloudReturnDocumentType ??
          SendcloudReturnDocumentType.BOTH,
        fedexQrCodesEnabledForMerchantRates:
          team.settings.fedexQrCodesEnabledForMerchantRates ?? false,
        useFixedPackageDimensions:
          team.settings.returns?.useFixedPackageDimensions ?? false,
        fixedPackageDimensions: {
          length:
            team.settings.returns?.fixedPackageDimensions?.length?.toString() ??
            "",
          width:
            team.settings.returns?.fixedPackageDimensions?.width?.toString() ??
            "",
          height:
            team.settings.returns?.fixedPackageDimensions?.height?.toString() ??
            "",
          weight:
            team.settings.returns?.fixedPackageDimensions?.weight?.toString() ??
            "",
        },
      },
      billingType: {
        subscriptionEnabled: team.settings.subscriptionEnabled,
        coverageBillingTrigger:
          team.automation?.coverageBillingTrigger ??
          CoverageBillingTrigger.ORDER_CREATION,
        redoItemFulfillment:
          team.automation?.fulfill_redo_item ?? RedoItemFulfillment.PARTIAL,
      },
      storefront: {
        attachmentStrategy:
          team.settings.shopifyExtension?.attachmentStrategy ??
          AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT,
        toggleSubtextEnabled:
          team.settings.shopifyExtension?.toggleSubtextEnabled ?? true,
        BothProductOptions: {
          productTitle:
            team.settings.shopifyExtension?.bothProductNameOverride || "",
          toggleMainText:
            team.settings.shopifyExtension?.toggleTextOptions?.bothProductToggle
              ?.titleText || "",
          toggleSubTextPositive:
            team.settings.shopifyExtension?.toggleTextOptions?.bothProductToggle
              ?.positiveSubtext || "",
          toggleSubTextNegative:
            team.settings.shopifyExtension?.toggleTextOptions?.bothProductToggle
              ?.negativeSubtext || "",
          sku: team.settings.combinedSKU || "",
        },
        cartToggle: {
          useShadowRootInCart: team.settings.useShadowRootInCart || false,
          cartShadowRootSelector: team.settings.cartShadowRootSelector || "",
          selector: team.settings.cartToggleSelector || "",
          placement:
            team.settings.cartTogglePlacement || InsertionPosition.BEFORE,
          clickSelector: team.settings.cartClickListenerSelector || "",
          toggleOnColor: team.settings.toggleOnColor || "",
          toggleOffColor: team.settings.toggleOffColor || "",
          toggleCircleColor: team.settings.toggleCircleColor || "",
          cartReplaceItems: team.settings.cartReplaceItems ?? false,
        },
        checkbox: {
          default: team.settings.redoAutoCheck,
          html: team.settings.html || "",
          selector: team.settings.checkboxSelector || "",
          productName: team.settings.productName || "",
          text: team.settings.checkboxText || "",
          hideCheckboxBranding: team.settings.hideCheckboxBranding || false,
          hideCheckboxPrice: team.settings.hideCheckboxPrice || false,
          textAfterRedo: team.settings.checkboxTextAfterRedo || "",
        },
        checkboxInfoModal: {
          text1: team.settings.modalTextAdjustments?.text1 || "",
          title1: team.settings.modalTextAdjustments?.title1 || "",
          text2: team.settings.modalTextAdjustments?.text2 || "",
          title2: team.settings.modalTextAdjustments?.title2 || "",
          text3: team.settings.modalTextAdjustments?.text3 || "",
          title3: team.settings.modalTextAdjustments?.title3 || "",
          logo: team.settings.modalLogo
            ? new URL(team.settings.modalLogo)
            : undefined,
          cartIconEnabled: team.settings.infoModal?.cartIconEnabled ?? false,
          checkoutIconEnabled:
            team.settings.infoModal?.checkoutIconEnabled ?? false,
          newLogo: team.settings.infoModal?.logo
            ? new URL(team.settings.infoModal.logo)
            : undefined,
          sideImage: team.settings.infoModal?.sideImage
            ? new URL(team.settings.infoModal.sideImage)
            : undefined,
          version: team.settings.infoModal?.version || InfoModalVersion.V1,
        },
        hideRedoProduct: {
          enabled: !!team.settings.shopifyExtension?.hideRedoProduct?.enabled,
          productSelector:
            team.settings.shopifyExtension?.hideRedoProduct?.productSelector ||
            "",
          cartCountModifications:
            team.settings.shopifyExtension?.hideRedoProduct?.cartCountModifications?.map(
              (x) => ({ id: Symbol(), ...x }),
            ) || [],
          cartPriceModifications:
            team.settings.shopifyExtension?.hideRedoProduct?.cartPriceModifications?.map(
              (x) => ({ id: Symbol(), ...x }),
            ) || [],
          cartDiscountModifications:
            team.settings.shopifyExtension?.hideRedoProduct?.cartDiscountModifications?.map(
              (x) => ({ id: Symbol(), ...x }),
            ) || [],
        },
        packageProtectionAutoCheck:
          team.settings.packageProtectionAutoCheck ??
          team.settings.redoAutoCheck,
        PackageProtectionProductOptions: {
          productTitle:
            team.settings.shopifyExtension?.packageProtectionNameOverride || "",
          toggleMainText:
            team.settings.shopifyExtension?.toggleTextOptions
              ?.packageProtectionToggle?.titleText || "",
          toggleSubTextPositive:
            team.settings.shopifyExtension?.toggleTextOptions
              ?.packageProtectionToggle?.positiveSubtext || "",
          toggleSubTextNegative:
            team.settings.shopifyExtension?.toggleTextOptions
              ?.packageProtectionToggle?.negativeSubtext || "",
          sku: team.settings.packageProtectionSKU || "",
        },
        returnProductOptions: {
          productTitle:
            team.settings.shopifyExtension?.returnNameOverride || "",
          toggleMainText:
            team.settings.shopifyExtension?.toggleTextOptions?.returnToggle
              ?.titleText || "",
          toggleSubTextPositive:
            team.settings.shopifyExtension?.toggleTextOptions?.returnToggle
              ?.positiveSubtext || "",
          toggleSubTextNegative:
            team.settings.shopifyExtension?.toggleTextOptions?.returnToggle
              ?.negativeSubtext || "",
          sku: team.settings.returnsSKU || "",
        },
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        shopifyProductSource: team.shopifyCoverageProductSource!,
        sku2: team.settings.sku2 || "",
        splitProducts: team.settings.packageProtection?.splitProducts ?? true,
        customPdpCss: team.settings.shopifyExtension?.customPdpCss || "",
        customToggleCss: team.settings.shopifyExtension?.customToggleCss || "",
        uniqueVariantSKU: team.settings.uniqueVariantSKU ?? false,
        indexBasedUniqueVariantStartingAt:
          team.settings.indexBasedUniqueVariantStartingAt,
        usingCartAndCheckoutToggle:
          team.settings.shopifyExtension?.usingCartAndCheckoutToggle ?? false,
        autoNavigateToCheckoutAfterTimeout:
          team.settings.shopifyExtension?.autoNavigateToCheckoutAfterTimeout ??
          true,
        redoVariantBaseName:
          team.settings.shopifyExtension?.redoVariantBaseName || "",
        variantNaming: getCoverageVariantNaming(team.settings.shopifyExtension),
        trackRedoProductInventory:
          team.settings.shopifyExtension?.trackRedoProductInventory ?? false,
        lockProduct: team.settings.shopifyExtension?.lockProduct ?? false,
        allowNonRedoVendorOnProduct:
          team.settings.shopifyExtension?.allowNonRedoVendorOnProduct ?? false,
      },
      ris: risForm,
      returnLabels: {
        disableMerchantPaidUpcharge:
          team.settings.returns?.removeMerchantPaidLabelUpcharge ?? false,
        rateSourceSelection: {
          enabled: team.settings.returns?.rateSourceSelection?.enabled ?? false,
        },
        merchantCarrierReturnLabels: team.settings.returns
          ?.merchantCarrierReturnLabels ?? {
          mode: MerchantCarrierReturnLabelMode.ONLY_REDO_CARRIERS,
          whitelistedCountries: [],
        },
      },
      redoMaxLabelPrice:
        team.settings.returns?.redoMaxLabelPrice?.toString() ?? "",
      multipleLabelsEnabled:
        team.settings.returns?.multipleLabelsEnabled ?? false,
      additionalCoverageWebSources:
        team.settings.additionalCoverageWebSources || [],
      exchangeAgentBillingSettings: createExchangeAgentBillingSettingsFormValue(
        team.settings.returnsAiExchange,
      ),
      subscriptionBilling: subscriptionBillingSettingsToFormValue(
        team.settings.returns?.subscriptionBilling,
      ),
      perReturnBillingSettings: perReturnBillingSettingsFormTeamToValue(team),
      exchangeOutboundLabelCreditSettings: {
        enabled: team.settings.exchangeOutboundLabelCredit?.enabled ?? false,
        amount: (
          team.settings.exchangeOutboundLabelCredit?.amount ?? 0
        ).toString(),
      },
    };
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team, risIntegrationLoad.value]);

  const [apiLoading, setApiLoading] = useState(true);
  const isLoading = apiLoading || !value || !team;

  useLayoutEffect(() => {
    const loadAll = async () => {
      setApiLoading(true);
      try {
        await Promise.all([
          loadTeam(),
          loadOnboarding(),
          loadCoverageMonitoring(),
          loadRisIntegration(),
        ]);
      } catch (error) {
        console.error("Failed to load data:", error);
      } finally {
        setApiLoading(false);
      }
    };
    void loadAll();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  // ANCHOR FORM

  if (isLoading) {
    return null;
  }

  return value ? (
    <GeneralForm
      coverageMonitor={coverageMonitoringLoad.value}
      hasCarrierServices={hasCarrierServices.value ?? false}
      hasShopifyIntegration={
        risIntegrationLoad.value?.hasShopifyIntegration === true
      }
      id={teamId}
      initialTeam={team}
      onboarding={onboardingLoad.value}
      onSave={handleSave}
      storefrontPlatform={storefrontPlatform}
      uncoveredWarnings={uncoveredWarningsLoad.value?.warnings ?? []}
      value={value}
    />
  ) : null;
});

const GeneralForm = memo(function GeneralForm({
  onSave,
  id,
  onboarding,
  coverageMonitor,
  value,
  hasShopifyIntegration,
  hasCarrierServices,
  storefrontPlatform,
  initialTeam,
  uncoveredWarnings,
}: {
  id: string;
  value: ReturnsPackageProtectionValue | undefined;
  onboarding: Onboarding | undefined;
  coverageMonitor: CoverageMonitorType | undefined;
  onSave?: (value: ReturnsPackageProtectionValue) => void;
  hasShopifyIntegration: boolean;
  hasCarrierServices: boolean;
  storefrontPlatform: StorefrontPlatform | undefined;
  initialTeam: Team;
  uncoveredWarnings: z.infer<typeof adminUncoveredSalesChannelWarningSchema>[];
}) {
  const client = useRequiredContext(RedoAdminClientContext);
  // const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const formInitialValue = value || returnsPackageProtectionDefault;
  const input = useInput(returnsPackageProtectionForm, formInitialValue);
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const merchantAppLogin = useMerchantAppLogin();

  const {
    returnsGeneral,
    packageProtection,
    finalSaleReturns,
    shipping,
    billingType,
    storefront,
    returnsCoverage,
    returnLabels,
    additionalCoverageWebSources,
    exchangeAgentBillingSettings,
    ris,
    subscriptionBilling,
  } = input.inputs;

  useEffect(() => {
    if (!value) {
      return;
    }
    // Overwrite the input value with the value from the server.
    // This is because we're using unique symbols as keys so they'll never be equal.
    if (value.storefront.hideRedoProduct?.cartCountModifications) {
      input.inputs.storefront.inputs.hideRedoProduct.inputs.cartCountModifications.setValue(
        value.storefront.hideRedoProduct.cartCountModifications,
      );
    }
    if (value.storefront.hideRedoProduct?.cartPriceModifications) {
      input.inputs.storefront.inputs.hideRedoProduct.inputs.cartPriceModifications.setValue(
        value.storefront.hideRedoProduct.cartPriceModifications,
      );
    }
    if (value.storefront.hideRedoProduct?.cartDiscountModifications) {
      input.inputs.storefront.inputs.hideRedoProduct.inputs.cartDiscountModifications.setValue(
        value.storefront.hideRedoProduct.cartDiscountModifications,
      );
    }

    if (value.returnsCoverage.internationalPricing) {
      input.inputs.returnsCoverage.inputs.internationalPricing.setValue(
        value.returnsCoverage.internationalPricing,
      );
    }

    if (value.packageProtection.pricingRuleSet) {
      input.inputs.packageProtection.inputs.pricingRuleSet.setValue(
        value.packageProtection.pricingRuleSet,
      );
      if (value.packageProtection.internationalPricingEnabled !== undefined) {
        input.inputs.packageProtection.inputs.internationalPricingEnabled.setValue(
          value.packageProtection.internationalPricingEnabled,
        );
      }
    }

    if (value.exchangeAgentBillingSettings) {
      input.inputs.exchangeAgentBillingSettings.inputs.billingTier.setValue(
        value.exchangeAgentBillingSettings.billingTier,
      );
      input.inputs.exchangeAgentBillingSettings.inputs.enabled.setValue(
        value.exchangeAgentBillingSettings.enabled,
      );
      input.inputs.exchangeAgentBillingSettings.inputs.enabledBilling.setValue(
        value.exchangeAgentBillingSettings.enabledBilling,
      );
      input.inputs.exchangeAgentBillingSettings.inputs.renewOnPeriodEnd.setValue(
        value.exchangeAgentBillingSettings.renewOnPeriodEnd,
      );
      input.inputs.exchangeAgentBillingSettings.inputs.startDate.setValue(
        value.exchangeAgentBillingSettings.startDate,
      );
      input.inputs.exchangeAgentBillingSettings.inputs.usageFee.setValue(
        value.exchangeAgentBillingSettings.usageFee,
      );
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const save: PageFormSave = useHandler(async (signal) => {
    let team = await getTeam(client, { teamId: id, signal });

    const formValue = input.value;

    const prevPackagePickupEnabled = cloneDeep(team.settings.pickupEnabled);

    team = returnsPackageProtectionFormValueToTeam({
      prevTeamValue: team,
      formValue,
    });

    team.settings.perReturnBilling = perReturnBillingSettingsFormValueToTeam(
      formValue.perReturnBillingSettings,
    );
    team.settings.exchangeOutboundLabelCredit = {
      enabled: formValue.exchangeOutboundLabelCreditSettings.enabled,
      amount: Number(formValue.exchangeOutboundLabelCreditSettings.amount) || 0,
    };

    await updateTeam(client, { teamId: id, team, signal });

    //Ris setup
    if (hasShopifyIntegration) {
      const shopifyIntegrationResult =
        await rpcClient.getShopifyIntegrationSettings(
          { teamId: id, integrationType: "Shopify" },
          { signal },
        );
      let risIntegration = shopifyIntegrationResult.risIntegration;

      const exchangePaymentStrategyChanged =
        formValue.returnsGeneral.exchangePaymentStrategy !== null &&
        formValue.returnsGeneral.exchangePaymentStrategy !==
          shopifyIntegrationResult.exchangePaymentStrategy;

      if (!isEqual(formValue.ris, risIntegration)) {
        //If the new shipping coverage is not defined, but it is defined on backend, just disable it so they won't lose their changes.
        if (!formValue.ris && risIntegration) {
          risIntegration.enabled = false;
        } else {
          risIntegration = {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            ...formValue.ris!,
            enabled:
              formValue.ris?.coverage === CoverageProductEnum.SHIPPING &&
              formValue.ris?.enabled === true,
          };
        }

        //If we enable/disable package protection or returns coverage we must update the RIS integration
        for (const rateTable of risIntegration.rateTables) {
          rateTable.coverageTypes = {
            packageProtection:
              formValue.packageProtection.enabled &&
              formValue.packageProtection.coverage,
            return: formValue.returnsGeneral.enabled,
          };

          if (!isCustomRateTable(rateTable)) {
            for (const rate of rateTable.rates) {
              if (rate.merchantPaidCoverageTypes) {
                if (!formValue.packageProtection.enabled) {
                  rate.merchantPaidCoverageTypes.packageProtection = undefined;
                }
                if (!formValue.returnsGeneral.enabled) {
                  rate.merchantPaidCoverageTypes.return = undefined;
                }
              }
            }
          }
        }

        await rpcClient.updateShopifyIntegrationSettings(
          {
            teamId: id,
            integrationType: "Shopify",
            integration: risIntegration,
            exchangePaymentStrategy:
              formValue.returnsGeneral.exchangePaymentStrategy ?? undefined,
          },
          { signal },
        );
      } else if (exchangePaymentStrategyChanged) {
        await rpcClient.updateShopifyIntegrationSettings(
          {
            teamId: id,
            integrationType: "Shopify",
            integration: risIntegration,
            exchangePaymentStrategy:
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              formValue.returnsGeneral.exchangePaymentStrategy!,
          },
          { signal },
        );
      }
    }
    // FIXME: Josh -> Michael update rate tables when PP changes on the PP page

    if (prevPackagePickupEnabled && !formValue.shipping.pickupEnabled) {
      await notifySlack(client, { teamId: id, teamName: team.name, signal });
    }

    onSave?.(formValue);
  });

  const getTreatment = useHandler(async () => {
    const currentTeam = await getTeam(client, { teamId: id });
    const value = input.value;
    return {
      name: "temp",
      weight: 1,
      active: false,
      settings: {
        redoEnabled: true,
        returnEnabled: value.returnsGeneral.enabled,
        packageProtectionEnabled: value.packageProtection.enabled,
        attachmentStrategy: value.storefront.attachmentStrategy,
        toggleTextOptions: {
          returnToggle: {
            titleText: value.storefront.returnProductOptions.toggleMainText,
            positiveSubtext:
              value.storefront.returnProductOptions.toggleSubTextPositive,
            negativeSubtext:
              value.storefront.returnProductOptions.toggleSubTextNegative,
          },
          packageProtectionToggle: {
            titleText:
              value.storefront.PackageProtectionProductOptions.toggleMainText,
            positiveSubtext:
              value.storefront.PackageProtectionProductOptions
                .toggleSubTextPositive ?? "",
            negativeSubtext:
              value.storefront.PackageProtectionProductOptions
                .toggleSubTextNegative ?? "",
          },
          bothProductToggle: {
            titleText: value.storefront.BothProductOptions.toggleMainText ?? "",
            positiveSubtext:
              value.storefront.BothProductOptions.toggleSubTextPositive ?? "",
            negativeSubtext:
              value.storefront.BothProductOptions.toggleSubTextNegative ?? "",
          },
        },
        hideBranding: currentTeam.settings.hidePortalBranding,
        cartElementPlacementSelector: value.storefront.cartToggle.selector,
        cartListenerSelector: value.storefront.cartToggle.clickSelector,
        cartElementPlacement: value.storefront.cartToggle.placement,
        hideCartProduct: value.storefront.hideRedoProduct.enabled,
        cartElementImage: undefined, //@Samuel TODO this should be a field on the admin dash
        redoAutoEnable: value.storefront.checkbox.default,
        packageProtectionAutoEnable:
          value.storefront.packageProtectionAutoCheck,
        hideOnPDP: true, //@Samuel TODO this should be a field on the admin dash
        usingCartAndCheckoutToggle: undefined, //@Samuel TODO this should be a field on the admin dash
        customToggleCss: value.storefront.customToggleCss,
      },
    };
  });

  const [pendingChangesTooltip, loadPendingChangesTooltip] = useTriggerLoad(
    async (signal: AbortSignal) => {
      if (!input.changed) {
        return null;
      }
      const team = await getTeam(client, { teamId: id, signal });

      const newTeam = returnsPackageProtectionFormValueToTeam({
        prevTeamValue: team,
        formValue: input.value,
      });

      const settingsChanges = getChanges({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        prev: team.settings as unknown as AuditableSettings,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        next: newTeam.settings as unknown as AuditableSettings,
      });
      const automationChanges = getChanges({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        prev: team.automation as unknown as AuditableSettings,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        next: newTeam.automation as unknown as AuditableSettings,
      }).map((change) => ({ ...change, path: `automation.${change.path}` }));
      const pendingChanges = [...settingsChanges, ...automationChanges];
      return <TeamChangesTooltip changes={pendingChanges} />;
    },
  );

  useEffect(() => {
    loadPendingChangesTooltip();
  }, [loadPendingChangesTooltip, input.value]);

  const widgets = initialTeam.settings.shopifyExtension?.widgetConfigs ?? [];

  const navigate = useNavigate();

  const enabledProducts = useMemo(() => {
    return [
      returnsGeneral.value.enabled,
      packageProtection.value.enabled,
      finalSaleReturns.value.enabled,
    ].filter(Boolean);
  }, [
    returnsGeneral.value.enabled,
    packageProtection.value.enabled,
    finalSaleReturns.value.enabled,
  ]);

  return (
    <TabNavigationProvider>
      <Flex
        align="flex-start"
        className={styles.pageContainer}
        dir="row"
        gap="none"
      >
        <SettingSectionTabNav />
        <div className={styles.settingsContent}>
          <PageForm
            additionalActions={
              <PreviewButton getTreatment={getTreatment} teamId={id} />
            }
            initial={value}
            input={input}
            save={save}
            saveButtonHoverContentSlot={pendingChangesTooltip.value}
          >
            {uncoveredWarnings.length > 0 &&
              (() => {
                const names = uncoveredWarnings.map((w) => w.displayName);
                const formatChannelList = (parts: string[]) => {
                  if (parts.length === 1) {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    return parts[0]!;
                  }
                  if (parts.length === 2) {
                    return `${parts[0]} and ${parts[1]}`;
                  }
                  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
                };
                const title =
                  uncoveredWarnings.length === 1
                    ? `${names[0]} is not enabled for coverage`
                    : `${formatChannelList(names)} are not enabled for coverage`;
                const description =
                  uncoveredWarnings.length === 1
                    ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      `${Math.round(uncoveredWarnings[0]!.percentage * 100)}% of recent orders come from ${names[0]}, but it isn't enabled for Redo coverage.`
                    : `${formatChannelList(uncoveredWarnings.map((w) => `${w.displayName} (${Math.round(w.percentage * 100)}%)`))} have recent orders but aren't enabled for Redo coverage.`;
                return (
                  <RedoAlert
                    actions={[
                      {
                        text: "Go to coverage settings",
                        action: () =>
                          merchantAppLogin(id, "/settings/coverage"),
                      },
                    ]}
                    color="error"
                    description={description}
                    fullWidth
                    title={title}
                  />
                );
              })()}
            <SettingSection
              description="Enable or disable coverage for your store"
              id="billing"
              title="Billing"
            >
              <BillingTypeSettings billingType={billingType} />
            </SettingSection>
            <SettingSection description="" id="products" title="Products">
              <SettingCard>
                <Flex align="stretch" dir="column" gap="none">
                  <Flex align="stretch" dir="column" gap="none" p="xl">
                    <Text fontSize="sm" fontWeight="medium" textColor="primary">
                      {`${enabledProducts.length} ${enabledProducts.length === 1 ? "product" : "products"} enabled`}
                    </Text>
                  </Flex>
                </Flex>
                <Flex align="stretch" dir="column" gap="none">
                  <ProtectionProductSummary
                    coverageEnabled={
                      returnsGeneral.value.enabled &&
                      initialTeam.settings.coverageEnabled &&
                      (!!initialTeam.settings.coverage.exchange ||
                        !!initialTeam.settings.coverage.refund ||
                        !!initialTeam.settings.coverage.storeCredit)
                    }
                    enabledCountries={getStandardReturnsEnabledCountries(
                      initialTeam,
                    )}
                    linkTo="../return-protection"
                    price={getStandardReturnsPrice({
                      returns: returnsCoverage?.value,
                      currency: getShopCurrencyWithDefault({
                        team: initialTeam,
                      }),
                    })}
                    productEnabled={returnsGeneral.value.enabled}
                    setProductEnabled={returnsGeneral.inputs.enabled.setValue}
                    title="Returns"
                  />
                  <ProtectionProductSummary
                    coverageEnabled={
                      packageProtection.value.coverage &&
                      packageProtection.value.enabled
                    }
                    enabledCountries={getPackageProtectionCountries(
                      packageProtection.value,
                    )}
                    linkTo="../package-protection"
                    price={getPackageProtectionPrice(initialTeam)}
                    productEnabled={packageProtection.value.enabled}
                    setProductEnabled={
                      packageProtection.inputs.enabled.setValue
                    } // FIXME: Josh - set enabled
                    title="Package protection"
                  />
                  <ProtectionProductSummary
                    coverageEnabled={finalSaleReturns.value.enabled}
                    enabledCountries={getRulesetCountries(
                      finalSaleReturns.value.pricingRuleSet,
                    )}
                    linkTo="../fsr-protection"
                    price={getFSRPrice(initialTeam)}
                    productEnabled={finalSaleReturns.value.enabled}
                    setProductEnabled={finalSaleReturns.inputs.enabled.setValue}
                    title="Final sale returns"
                  />
                </Flex>
              </SettingCard>
            </SettingSection>
            <SettingSection description="" id="storefront" title="Storefront">
              <StorefrontPublishConfigurator
                onSaved={() => onSave?.(input.value)}
                productKey="coverage"
                teamId={id}
              />
              <div>
                <AttachmentStrategyPicker
                  input={storefront.inputs.attachmentStrategy}
                  platform={storefrontPlatform}
                  splitProducts={storefront.value.splitProducts}
                />
              </div>
              <div>
                <RedoToggle
                  description="Creates separate package protection and return coverage products and allows them to be sold separately"
                  label="Split products"
                  setValue={storefront.inputs.splitProducts.setValue}
                  size="sm"
                  value={storefront.inputs.splitProducts.value}
                />
              </div>

              <SettingCard>
                <Flex align="stretch" dir="column" gap="none">
                  <LinkCard
                    onClick={() => navigate("../product")}
                    subtitle="Product titles, skus, etc."
                    title="Shopify Product"
                  />
                </Flex>
                <Flex align="stretch" dir="column" gap="none">
                  <LinkCard
                    onClick={() => navigate("../cart-modifications")}
                    subtitle="Hiding elements, price overrides, etc."
                    title="Cart Modifications"
                  />
                </Flex>
                <Flex align="stretch" dir="column" gap="none">
                  <LinkCard
                    onClick={() =>
                      merchantAppLogin(
                        id,
                        "/settings/coverage/coverage-builder",
                      )
                    }
                    subtitle="Customize coverage widget placement and display"
                    title="Coverage Builder"
                  />
                </Flex>
              </SettingCard>
              <RedoCollapseSubsection title="Legacy Widgets Config">
                <div className={styles.legacyWidgetContainer}>
                  <WidgetSelect widgets={widgets} />
                  <ToggleWidgetItem />
                  <PDPWidgetItem />
                  <AddWidgetButton />
                </div>
              </RedoCollapseSubsection>
            </SettingSection>
            {!billingType.value.subscriptionEnabled &&
            (storefront?.value.attachmentStrategy ===
              AttachmentStrategy.SHIPPING ||
              storefront?.value.attachmentStrategy ===
                AttachmentStrategy.CHECKOUT_BUTTON_SHIPPING) ? (
              <SettingSection
                description=""
                id="shipping-zones"
                title="Shipping zones"
              >
                <ShippingZones
                  hasCarrierServices={hasCarrierServices}
                  hasReturnsCoverage={returnsGeneral.value.enabled}
                  input={ris}
                  packageProtectionInput={packageProtection}
                  teamId={id}
                />
              </SettingSection>
            ) : null}
            <SettingSection
              description=""
              id="portal-processing"
              title="Portal / Processing"
            >
              <ReturnsGeneralCard
                hasShopifyIntegration={hasShopifyIntegration}
                input={returnsGeneral}
              />
              <CoverageBillingSettings billingType={billingType} />
              <YofiIntegrationCard input={input.inputs.yofi} />
            </SettingSection>
            <SettingSection
              description="Copy and publish the most recently published return flow from this team to another team."
              id="copy-return-flow"
              title="Copy return flow"
            >
              <CopyLatestFlowSettings
                flowType="return"
                sourceTeamId={id}
                sourceTeamName={initialTeam.name}
              />
            </SettingSection>
            <SettingSection
              description="Copy this team's Portal Builder styling to another team. Useful for merchants running multiple stores."
              id="copy-portal-customization"
              title="Copy portal customization"
            >
              <CopyPortalCustomizationSettings
                fixedSourceTeamId={id}
                fixedSourceTeamName={initialTeam.name}
              />
            </SettingSection>
            <SettingSection
              description=""
              id="ai-exchange-billing"
              title="AI exchange billing"
            >
              <ExchangeAgentBillingSettingsCard
                input={exchangeAgentBillingSettings}
              />
            </SettingSection>
            <SettingSection
              description="Custom source names that should be treated as web orders for coverage monitoring. These will be used in addition to the default 'web' source. Enter one source name per line."
              id="coverage-web-sources"
              title="Coverage web sources"
            >
              <AdditionalCoverageWebSourcesSettings
                input={additionalCoverageWebSources}
              />
            </SettingSection>
            <SettingSection
              description={`Pauses every coverage offer (returns, package protection, final sale) on the storefront, checkout and public API for up to ${MAX_MERCHANT_FEATURE_PAUSE_DAYS} days without changing the merchant's settings. Merchant-paid protection stops being applied to new orders too. Orders placed before the pause keep the coverage they already have.`}
              id="coverage-pause"
              title="Coverage pause"
            >
              <CoveragePauseCard teamId={id} />
            </SettingSection>
            <SettingSection
              description="Enable automated storefront inspections."
              id="coverage-qa"
              title="Coverage QAgent"
            >
              <CoverageQaCard teamId={id} />
            </SettingSection>
            {coverageMonitor?.current && (
              <SettingSection
                description=""
                id="coverage-monitoring"
                title="Coverage monitoring"
              >
                <CoverageMonitorSettings
                  coverageMonitor={coverageMonitor}
                  onButtonClick={() =>
                    handleCoverageMonitoringReset(id, client)
                  }
                />
              </SettingSection>
            )}
            <SettingSection
              description=""
              id="onboarding-statistics"
              title="Onboarding statistics"
            >
              <OnboardingStats
                input={returnsCoverage}
                onboarding={onboarding}
              />
              <OnboardingOrderCountCard teamId={id} />
            </SettingSection>
            <SettingSection
              description=""
              id="per-return-billing"
              title="Per-return billing"
            >
              <PerReturnBillingSettingsCardV2
                input={input.inputs.perReturnBillingSettings}
              />
            </SettingSection>
            <SettingSection
              description="Fixed invoice credit for each processed exchange of an order with Redo returns coverage. Redo pays it. Uncovered orders are skipped."
              id="exchange-outbound-label-credit"
              title="Exchange outbound label credit"
            >
              <ExchangeOutboundLabelCreditSettingsCard
                input={input.inputs.exchangeOutboundLabelCreditSettings}
              />
            </SettingSection>
            <SettingSection
              description=""
              id="subscription-billing"
              title="Subscription billing"
            >
              <SubscriptionBillingCardV2 input={subscriptionBilling} />
            </SettingSection>
            <SettingSection
              description=""
              id="return-labels"
              title="Return labels"
            >
              <ReturnShippingSettings
                input={shipping}
                multipleLabelsEnabledInput={input.inputs.multipleLabelsEnabled}
                redoMaxLabelPriceInput={input.inputs.redoMaxLabelPrice}
              />
              <LabelRatesCard input={returnLabels} />
            </SettingSection>
          </PageForm>
        </div>
      </Flex>
    </TabNavigationProvider>
  );
});

const getRulesetCountries = (
  ruleset: FinalSaleReturnsValue["pricingRuleSet"],
): CountryCode[] => [
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  ...new Set(ruleset.flatMap((rule) => rule.countries as CountryCode[])),
];

const getFSRPrice = (team: Team): string => {
  const currency = getShopCurrencyWithDefault({ team });
  const minPrice = team.settings.returns?.finalSaleReturns?.minPrice ?? 0;
  const maxPrice = team.settings.returns?.finalSaleReturns?.maxPrice ?? 0;

  if (
    team.settings.returns?.finalSaleReturns?.pricingRuleSet?.length &&
    team.settings.returns?.finalSaleReturns?.pricingRuleSet?.[0]?.priceBrackets
      ?.length === 1 &&
    team.settings.returns?.finalSaleReturns?.pricingRuleSet?.[0]?.type ===
      "fixed"
  ) {
    return moneyFormat({
      amount:
        team.settings.returns?.finalSaleReturns?.pricingRuleSet?.[0]
          ?.priceBrackets?.[0]?.value ?? 0,
      currency,
    });
  }

  if (!minPrice || !maxPrice) {
    return "";
  }

  const basePrice =
    team.settings.returns?.finalSaleReturns?.shippingLabelCoveragePrice ?? 0;

  if (minPrice === maxPrice) {
    return moneyFormat({ amount: minPrice + basePrice, currency });
  }

  return `${moneyFormat({ amount: minPrice + basePrice, currency })} - ${moneyFormat({ amount: maxPrice + basePrice, currency })}`;
};

const getPackageProtectionPrice = (team: Team): string => {
  const currency = getShopCurrencyWithDefault({ team });
  const minPrice = team.settings.packageProtection?.minPrice ?? 0;
  const maxPrice = team.settings.packageProtection?.maxPrice ?? 0;

  const pricePerItem = team.settings.packageProtection?.pricePerItem;
  if (
    team.settings.packageProtection?.pricingStrategy ===
      PackageProtectionPricingStrategy.PRICE_PER_ITEM &&
    pricePerItem
  ) {
    return `${moneyFormat({ amount: pricePerItem.basePrice, currency })} first item + ${moneyFormat({ amount: pricePerItem.pricePerAdditionalItem, currency })}/additional, max ${moneyFormat({ amount: pricePerItem.maxPrice, currency })}`;
  }

  if (
    team.settings.packageProtection?.pricingRuleSet?.length &&
    team.settings.packageProtection?.pricingRuleSet?.[0]?.priceBrackets
      ?.length === 1 &&
    team.settings.packageProtection?.pricingRuleSet?.[0]?.type === "fixed"
  ) {
    return moneyFormat({
      amount:
        team.settings.packageProtection?.pricingRuleSet?.[0]?.priceBrackets?.[0]
          ?.value ?? 0,
      currency,
    });
  }

  if (!minPrice || !maxPrice) {
    return "";
  }

  if (minPrice === maxPrice) {
    return moneyFormat({ amount: minPrice, currency });
  }

  return `${moneyFormat({ amount: minPrice, currency })} - ${moneyFormat({ amount: maxPrice, currency })}`;
};

const getStandardReturnsPrice = ({
  returns,
  currency,
}: {
  returns: ReturnsCoverageValue;
  currency: Currency;
}): string => {
  let minValue = 0;
  let maxValue = 0;
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (returns.dynamicPriceCartValueRuleSet?.[0].priceBrackets.length > 0) {
    minValue = Math.min(
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      ...returns.dynamicPriceCartValueRuleSet[0].priceBrackets.map((rule) =>
        Number(rule.value),
      ),
    );
    maxValue = Math.max(
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      ...returns.dynamicPriceCartValueRuleSet[0].priceBrackets.map((rule) =>
        Number(rule.value),
      ),
    );
  }
  switch (returns.returnPricingSelection) {
    case ReturnsPricingEnum.FIXED:
    case undefined:
      return moneyFormat({ amount: Number(returns.pricePerOrder), currency });
      break;
    case ReturnsPricingEnum.PER_ITEM:
      if (
        Number(returns.pricePerOrder) ===
          Number(returns.pricePerAdditonalItem) ||
        Number(returns.pricePerAdditonalItem) === 0
      ) {
        return moneyFormat({ amount: Number(returns.pricePerOrder), currency });
      }
      return `${moneyFormat({ amount: Number(returns.pricePerOrder), currency })} + ${moneyFormat({ amount: Number(returns.pricePerAdditonalItem), currency })} per additional item`;
    case ReturnsPricingEnum.ORDER_TOTAL:
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      if (returns.dynamicPriceCartValueRuleSet[0].type === "percentage") {
        return `${minValue}% – ${maxValue}%`;
      }

      return `${moneyFormat({ amount: minValue, currency })} - ${moneyFormat({ amount: maxValue, currency })}`;
    default:
      assertNever(returns.returnPricingSelection);
      break;
  }
};

const getStandardReturnsEnabledCountries = (initialTeam: Team): string[] => {
  const listOfListsOfCountries = [initialTeam.settings.countries];

  if (
    !!initialTeam.settings.returns?.internationalPricing &&
    !!initialTeam.settings.returns.internationalPricing.length
  ) {
    const listOfCountries: string[][] =
      initialTeam.settings.returns.internationalPricing.map(
        (pricing) => pricing.countries,
      );
    listOfListsOfCountries.push(...listOfCountries);
  }

  for (const countries of listOfListsOfCountries) {
    if (countries.length === 0) {
      return []; // All countries
    }
  }

  return [
    ...new Set([
      ...initialTeam.settings.countries,
      ...listOfListsOfCountries.flat(),
    ]),
  ];
};

const getPackageProtectionCountries = (
  packageProtection: PackageProtectionValue,
) => {
  const packageProtectionCountries: string[] = [];

  if (packageProtection?.pricingRuleSet.length) {
    for (const ruleset of packageProtection?.pricingRuleSet ?? []) {
      if (ruleset.countries.length) {
        packageProtectionCountries.push(...ruleset.countries);
      } else {
        return []; // All countries
      }
    }
  }
  return [...new Set(packageProtectionCountries)];
};

async function handleCoverageMonitoringReset(teamId: any, client: any) {
  console.log("Sending the resetCoverageMonitoring Request");
  await resetCoverageMonitoring(client, { teamId });
}

const returnsPackageProtectionFormValueToTeam = ({
  prevTeamValue,
  formValue,
}: {
  prevTeamValue: Team;
  formValue: ReturnsPackageProtectionValue;
}): Team => {
  const team = cloneDeep(prevTeamValue);

  team.settings.canEditReturnAddress = formValue.shipping.canEditReturnAddress;
  team.settings.checkboxText = formValue.storefront.checkbox.text || undefined;
  team.settings.html = formValue.storefront.checkbox.html || undefined;
  team.settings.redoAutoCheck = formValue.storefront.checkbox.default;
  team.settings.checkboxSelector =
    formValue.storefront.checkbox.selector || undefined;
  team.settings.checkboxTextAfterRedo =
    formValue.storefront.checkbox.textAfterRedo || undefined;
  team.settings.hideCheckboxBranding =
    formValue.storefront.checkbox.hideCheckboxBranding;
  team.settings.hideCheckboxPrice =
    formValue.storefront.checkbox.hideCheckboxPrice;
  team.settings.useShadowRootInCart =
    formValue.storefront.cartToggle.useShadowRootInCart;
  team.settings.cartShadowRootSelector =
    formValue.storefront.cartToggle.cartShadowRootSelector;
  team.settings.cartToggleEnabled =
    doesAttachmentStrategyRenderInCart[formValue.storefront.attachmentStrategy];
  team.settings.cartToggleSelector =
    formValue.storefront.cartToggle.selector || undefined;
  team.settings.cartTogglePlacement =
    formValue.storefront.cartToggle.placement || undefined;
  team.settings.cartClickListenerSelector =
    formValue.storefront.cartToggle.clickSelector || undefined;
  team.settings.toggleOnColor =
    formValue.storefront.cartToggle.toggleOnColor || "";
  team.settings.toggleOffColor =
    formValue.storefront.cartToggle.toggleOffColor || "";
  team.settings.toggleCircleColor =
    formValue.storefront.cartToggle.toggleCircleColor || "";
  team.settings.cartReplaceItems =
    formValue.storefront.cartToggle.cartReplaceItems;
  team.settings.productName =
    formValue.storefront.checkbox.productName || undefined;
  team.settings.subscriptionEnabled = formValue.billingType.subscriptionEnabled;
  team.automation = {
    ...team.automation,
    coverageBillingTrigger: formValue.billingType.coverageBillingTrigger,
    fulfill_redo_item: formValue.billingType.redoItemFulfillment,
  };
  team.settings.coverageEnabled = returnsCoverageIsEnabled(
    formValue.returnsCoverage.coverage,
    formValue.returnsGeneral.enabled,
  );

  team.settings.pickupEnabled = formValue.shipping.pickupEnabled;
  team.settings.pickupEnabledForHeavyItems =
    formValue.shipping.pickupEnabledForHeavyItems;
  team.settings.pickupMaxLabelUpgradeRange = formValue.shipping
    .pickupMaxLabelUpgradeRange
    ? +formValue.shipping.pickupMaxLabelUpgradeRange
    : undefined;
  team.settings.shippingCarrierOverride = formValue.shipping.carrierOverride;
  team.settings.includeMultipleCarrierOptions =
    formValue.shipping.includeMultipleCarrierOptions;
  team.settings.shippingInsurance = formValue.shipping.insured
    ? ShippingInsurance.ENABLED
    : ShippingInsurance.DISABLED;
  team.settings.generateQrCodes = formValue.shipping.generateQrCodes;
  team.settings.qrCodeOnlyShipping = formValue.shipping.qrCodeOnlyShipping;
  team.settings.sendcloudReturnDocumentType =
    formValue.shipping.sendcloudReturnDocumentType;
  team.settings.fedexQrCodesEnabledForMerchantRates =
    formValue.shipping.fedexQrCodesEnabledForMerchantRates;
  team.settings.protectionExcludeTags = formValue.packageProtection
    .protectionExcludeTags as string[];
  team.settings.packageProtection = {
    ...team.settings.packageProtection,
    ...packageProtectionFormValueToTeam(
      formValue.packageProtection,
      formValue.storefront.splitProducts,
    ),
  };
  team.settings.yofi = yofiFormValueToTeam(formValue.yofi);
  team.settings.customerPortalVersion =
    formValue.returnsGeneral.customerPortalVersion;

  team.settings.returns = {
    ...team.settings.returns,
    enabled: formValue.returnsGeneral.enabled,
    refundAllReturnedItems: formValue.returnsGeneral.refundAllReturnedItems,
    skipExchangeRefunds: formValue.returnsGeneral.skipExchangeRefunds,
    settlementEnabled: team.settings.returns?.settlementEnabled ?? false,
    allowMultiOrderReturns: formValue.returnsGeneral.allowMultiOrderReturns,
    multiOrderReturnLimit: Number(
      formValue.returnsGeneral.multiOrderReturnLimit,
    ),
    internationalPricing: formValue.returnsCoverage.internationalPricing.map(
      (price) => {
        return {
          countries: price.countries,
          multiplier: Math.floor(Number(price.multiplier) * 100) / 100,
        };
      },
    ),
    enableCustomerPortal: formValue.returnsGeneral.enableCustomerPortal,
    buyYourOwnLabel: formValue.returnsGeneral.buyYourOwnLabel,
    finalSaleReturns: finalSaleReturnsFormValueToTeam(
      formValue.finalSaleReturns,
    ),
    dontChargeMerchantForDamagedOrWrongProductReturnLabels:
      !formValue.returnsGeneral
        .chargeMerchantForDamagedOrWrongProductReturnLabels,
    merchantCarrierReturnLabels: {
      ...team.settings.returns?.merchantCarrierReturnLabels,
      ...formValue.returnLabels.merchantCarrierReturnLabels,
    },
    rateSourceSelection: {
      enabled: formValue.returnLabels.rateSourceSelection.enabled,
    },
    agentReviewEnabled: formValue.returnsGeneral.agentReviewEnabled,
    agentChatEnabled: formValue.returnsGeneral.agentChatEnabled,
    sendStoreCreditCreatedForGiftCards:
      formValue.returnsGeneral.sendStoreCreditCreatedForGiftCards,
    subscriptionBilling: subscriptionBillingFormValueToTeam(
      formValue.subscriptionBilling,
      team.settings.returns?.subscriptionBilling,
    ),
    passThroughLabelAdjustments:
      formValue.shipping.passThroughReturnLabelAdjustments,
    orderSkuMetafieldKey:
      formValue.returnsGeneral.orderSkuMetafieldKey || undefined,
    dutyDrawback: {
      enabled: formValue.returnsGeneral.dutyDrawbackEnabled,
      eligibleCountries:
        formValue.returnsGeneral.dutyDrawbackEligibleCountries.map(
          (country) => country.code,
        ),
    },
  };
  team.settings.bundleRulesList = parseBundleRulesJson(
    formValue.returnsGeneral.bundleRulesJson,
  );
  team.settings.exchanges.nonZeroValueExchangeStrategy =
    formValue.returnsGeneral.nonZeroValueExchangeStrategy;
  team.settings.exchanges.shopifyDeferReturnItemProcessing =
    formValue.returnsGeneral.shopifyDeferReturnItemProcessing;
  team.shopifyCoverageProductSource = formValue.storefront.shopifyProductSource;

  // NOTE: commented out package protection percentage being set during saving
  // TODO: test if this is needed. why the heck would we want this though?
  // input.inputs.packageProtection.inputs.percentage.setValue(
  //   String(round(team.settings.packageProtection.percentage * 100.0, 3)),
  // );

  team.settings.countriesPackageProtection =
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    formValue.packageProtection.pricingRuleSet[0].countries ?? [];
  team.settings.returnsSKU =
    formValue.storefront.returnProductOptions.sku || "";
  team.settings.packageProtectionSKU =
    formValue.storefront.PackageProtectionProductOptions.sku || "";
  team.settings.combinedSKU = formValue.storefront.BothProductOptions.sku || "";
  team.settings.uniqueVariantSKU =
    formValue.storefront.uniqueVariantSKU ?? false;
  team.settings.indexBasedUniqueVariantStartingAt =
    formValue.storefront.indexBasedUniqueVariantStartingAt;

  team.settings.shopifyExtension = {
    ...team.settings.shopifyExtension,
    customizationVisible:
      team.settings.shopifyExtension?.customizationVisible ?? false,
    customizeCartItem:
      team.settings.shopifyExtension?.customizeCartItem ?? false,
    brandPrefix: team.settings.shopifyExtension?.brandPrefix ?? "",
    bothProductNameOverride:
      formValue.storefront.BothProductOptions.productTitle ?? "",
    packageProtectionNameOverride:
      formValue.storefront.PackageProtectionProductOptions.productTitle ?? "",
    returnNameOverride:
      formValue.storefront.returnProductOptions.productTitle ?? "",
    redoVariantBaseName: formValue.storefront.redoVariantBaseName ?? "",
    variantNaming: getCoverageVariantNamingToSave(
      team.settings.shopifyExtension,
      formValue.storefront.variantNaming,
    ),
    toggleTextOptions: {
      returnToggle: {
        titleText:
          formValue.storefront.returnProductOptions.toggleMainText ?? "",
        positiveSubtext:
          formValue.storefront.returnProductOptions.toggleSubTextPositive ?? "",
        negativeSubtext:
          formValue.storefront.returnProductOptions.toggleSubTextNegative ?? "",
      },
      packageProtectionToggle: {
        titleText:
          formValue.storefront.PackageProtectionProductOptions.toggleMainText ??
          "",
        positiveSubtext:
          formValue.storefront.PackageProtectionProductOptions
            .toggleSubTextPositive ?? "",
        negativeSubtext:
          formValue.storefront.PackageProtectionProductOptions
            .toggleSubTextNegative ?? "",
      },
      bothProductToggle: {
        titleText: formValue.storefront.BothProductOptions.toggleMainText ?? "",
        positiveSubtext:
          formValue.storefront.BothProductOptions.toggleSubTextPositive ?? "",
        negativeSubtext:
          formValue.storefront.BothProductOptions.toggleSubTextNegative ?? "",
      },
    },
    attachmentStrategy: formValue.storefront.attachmentStrategy,
    toggleSubtextEnabled: formValue.storefront.toggleSubtextEnabled,
    hideRedoProduct: {
      enabled: formValue.storefront.hideRedoProduct.enabled,
      productSelector: formValue.storefront.hideRedoProduct.productSelector,
      checkoutButtonSelector:
        team.settings.shopifyExtension?.hideRedoProduct
          ?.checkoutButtonSelector ?? "",
      cartCountModifications:
        formValue.storefront.hideRedoProduct.cartCountModifications.map(
          (x) => ({
            selector: x.selector,
            search: x.search,
            replace: x.replace,
          }),
        ),
      cartPriceModifications:
        formValue.storefront.hideRedoProduct.cartPriceModifications.map(
          (x) => ({
            selector: x.selector,
            search: x.search,
            replace: x.replace,
          }),
        ),
      cartDiscountModifications:
        formValue.storefront.hideRedoProduct.cartDiscountModifications.map(
          (x) => ({
            selector: x.selector,
            search: x.search,
            replace: x.replace,
          }),
        ),
    },
    checkoutExtensionEnabled:
      isAttachmentStrategyCheckoutExtensionEnabled[
        formValue.storefront.attachmentStrategy
      ],
    customPdpCss: formValue.storefront.customPdpCss,
    customToggleCss: formValue.storefront.customToggleCss,
    usingCartAndCheckoutToggle: formValue.storefront.usingCartAndCheckoutToggle,
    autoNavigateToCheckoutAfterTimeout:
      formValue.storefront.autoNavigateToCheckoutAfterTimeout,
    trackRedoProductInventory: formValue.storefront.trackRedoProductInventory,
    allowNonRedoVendorOnProduct:
      formValue.storefront.allowNonRedoVendorOnProduct,
  };
  team.settings.packageProtectionAutoCheck =
    formValue.storefront.packageProtectionAutoCheck;
  team.settings.modalTextAdjustments = {
    text1: formValue.storefront.checkboxInfoModal.text1,
    title1: formValue.storefront.checkboxInfoModal.title1,
    text2: formValue.storefront.checkboxInfoModal.text2,
    title2: formValue.storefront.checkboxInfoModal.title2,
    text3: formValue.storefront.checkboxInfoModal.text3,
    title3: formValue.storefront.checkboxInfoModal.title3,
  };

  team.settings.infoModal = {
    ...team.settings.infoModal,
    cartIconEnabled: formValue.storefront.checkboxInfoModal.cartIconEnabled,
    checkoutIconEnabled:
      formValue.storefront.checkboxInfoModal.checkoutIconEnabled,
    version: formValue.storefront.checkboxInfoModal.version,
    logo: formValue.storefront.checkboxInfoModal.newLogo
      ? formValue.storefront.checkboxInfoModal.newLogo.toString()
      : "",
    sideImage: formValue.storefront.checkboxInfoModal.sideImage
      ? formValue.storefront.checkboxInfoModal.sideImage.toString()
      : "",
  };
  team.settings.modalLogo = formValue.storefront.checkboxInfoModal.logo
    ? formValue.storefront.checkboxInfoModal.logo.toString()
    : "";

  const returnsSettings = team.settings.returns;
  if (!returnsSettings) {
    throw new Error("team.settings.returns missing after assignment above");
  }
  returnsSettings.removeMerchantPaidLabelUpcharge =
    formValue.returnLabels.disableMerchantPaidUpcharge;
  returnsSettings.merchantCarrierReturnLabels = {
    ...returnsSettings.merchantCarrierReturnLabels,
    ...formValue.returnLabels.merchantCarrierReturnLabels,
  };
  returnsSettings.redoMaxLabelPrice = formValue.redoMaxLabelPrice
    ? Number(formValue.redoMaxLabelPrice)
    : undefined;
  returnsSettings.multipleLabelsEnabled = formValue.multipleLabelsEnabled;
  returnsSettings.useFixedPackageDimensions =
    formValue.shipping.useFixedPackageDimensions;
  returnsSettings.fixedPackageDimensions = formValue.shipping
    .useFixedPackageDimensions
    ? {
        length:
          Number(formValue.shipping.fixedPackageDimensions.length) || undefined,
        width:
          Number(formValue.shipping.fixedPackageDimensions.width) || undefined,
        height:
          Number(formValue.shipping.fixedPackageDimensions.height) || undefined,
        weight:
          Number(formValue.shipping.fixedPackageDimensions.weight) || undefined,
      }
    : undefined;

  // Update additional coverage web sources
  team.settings.additionalCoverageWebSources = [
    ...formValue.additionalCoverageWebSources,
  ];

  // Exchange Agent billing settings
  const updatedExchangeAgentBillingSettings =
    getUpdatedExchangeAgentBillingSettings(
      team.settings.returnsAiExchange,
      formValue.exchangeAgentBillingSettings,
    );
  team.settings.returnsAiExchange.enabled =
    updatedExchangeAgentBillingSettings.enabled;
  team.settings.returnsAiExchange.enableBilling =
    updatedExchangeAgentBillingSettings.enableBilling;
  team.settings.returnsAiExchange.renewOnPeriodEnd =
    updatedExchangeAgentBillingSettings.renewOnPeriodEnd;
  team.settings.returnsAiExchange.usageFee =
    updatedExchangeAgentBillingSettings.usageFee;
  team.settings.returnsAiExchange.usageFeePercent =
    updatedExchangeAgentBillingSettings.usageFeePercent;
  team.settings.returnsAiExchange.billingFeeType =
    updatedExchangeAgentBillingSettings.billingFeeType;
  team.settings.returnsAiExchange.billingType =
    updatedExchangeAgentBillingSettings.billingType;
  // Only worry about updating the start date if we are enabling billing.
  if (
    team.settings.returnsAiExchange.enableBilling &&
    team.settings.returnsAiExchange.renewOnPeriodEnd
  ) {
    team.settings.returnsAiExchange.startDate =
      updatedExchangeAgentBillingSettings.startDate;
  }

  return team;
};
